import { PropsWithChildren, ReactElement, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "./Button";
import { Modal } from "./Modal";
import { TranslatedString } from "../i18n";
import { Tenant } from "../types/Config";

type DialogButtonProps = {
    title: string;
    action: () => void;
};

export const Dialog = ({
    image,
    title,
    theme,
    primaryButton,
    secondaryButton,
    onBackgroundClick,
    dataTestId,
    className,
    children
}: PropsWithChildren<{
    dataTestId?: string;
    image?: ReactElement;
    title?: TranslatedString;
    theme?: Tenant | "kitchen";
    primaryButton?: DialogButtonProps;
    secondaryButton?: DialogButtonProps;
    className?: string;
    onBackgroundClick?: () => void;
}>) => {
    const [isOpen, setIsOpen] = useState(true);

    const onCloseCallbackRef = useRef<(() => void) | undefined>(undefined);

    const closeWith = (action: () => void) => () => {
        onCloseCallbackRef.current = action;
        setIsOpen(false);
    };

    return (
        <Modal
            theme={theme}
            isOpen={isOpen}
            onCloseComplete={() => onCloseCallbackRef.current?.()}
            onBackgroundClick={
                onBackgroundClick && closeWith(onBackgroundClick)
            }
        >
            <div
                data-testid={dataTestId}
                onClick={(e) => e.stopPropagation()}
                className={twMerge(
                    "flex w-80 flex-col items-center gap-6 rounded-xl bg-white p-6 shadow-lg",
                    className
                )}
            >
                {(image || title) && (
                    <div className="flex flex-col items-center">
                        {image && (
                            <div className="text-dialog-image [&>svg]:h-28">
                                {image}
                            </div>
                        )}

                        {title && (
                            <div className="text-center text-xl font-bold text-primary-800">
                                {title}
                            </div>
                        )}
                    </div>
                )}

                {children}

                {(primaryButton || secondaryButton) && (
                    <div className="-mx-3 -mb-3 flex gap-3 self-stretch [&>*]:flex-1">
                        {secondaryButton && (
                            <Button
                                variant="secondary"
                                data-testid="dialog-button-secondary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closeWith(secondaryButton.action)();
                                }}
                                className="p-3 text-sm font-semibold"
                            >
                                {secondaryButton.title}
                            </Button>
                        )}
                        {primaryButton && (
                            <Button
                                variant="primary"
                                data-testid="dialog-button-primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closeWith(primaryButton.action)();
                                }}
                                className="p-3 text-sm font-semibold"
                            >
                                {primaryButton.title}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export const ConfirmChangeDialog = ({
    title,
    confirmTitle,
    confirmAction,
    rejectTitle,
    rejectAction
}: PropsWithChildren<{
    title: string;
    confirmTitle: string;
    confirmAction: () => void;
    rejectTitle: string;
    rejectAction: () => void;
}>) => {
    return (
        <Dialog
            title={title}
            primaryButton={{
                title: confirmTitle,
                action: confirmAction
            }}
            secondaryButton={{
                title: rejectTitle,
                action: rejectAction
            }}
        />
    );
};
